/* eslint-disable no-nested-ternary */
/* eslint-disable @repo/no-style-props */
import * as React from "react";
import {
  ExternalLink,
  File,
  Image as ImageIcon,
  ImageOff,
  Paperclip,
  Video,
  Volume2,
  type LucideIcon,
} from "lucide-react";
import { cva } from "class-variance-authority";

import { Button } from "@/src/components/ui/button";
import { Skeleton } from "@/src/components/ui/skeleton";
import { ControlledHoverCard } from "@/src/components/design-system/ControlledHoverCard/ControlledHoverCard";
import { cn } from "@/src/utils/tailwind";
import { getMappedMediaLabel } from "@/src/fns/getMappedMediaLabel";

/**
 * Resolution state of the previewable content. The collapsed chip renders the
 * same regardless of status (it only needs `contentType`); status drives the
 * peek popover body. Owned by the container that fetches the URL — `MediaTag`
 * itself never fetches, which keeps it pure and Storybook-able.
 */
export type MediaTagStatus = "idle" | "loading" | "ready" | "error";
type MediaTagIntent = "default" | "attachment";
type MediaTagSize = "sm" | "md";

export interface MediaTagProps {
  /**
   * Full MIME type from the media reference (e.g. "image/png"). Known before
   * any fetch, so the chip + icon + label always render without a URL.
   */
  contentType: string;
  /** Peek-content resolution state. Defaults to "idle". */
  status?: MediaTagStatus;
  /**
   * Resolved URL for the preview and the "open in new tab" action. Only
   * meaningful when `status === "ready"`.
   */
  url?: string;
  /** Resolved file size in bytes, shown in the peek header when available. */
  contentLength?: number;
  /** Overrides the chip label (defaults to the MIME subtype, e.g. "JPEG"). */
  label?: string;
  /** Optional context shown above the preview in the peek popover. */
  description?: string;
  /** Optional raw reference shown inside the unavailable-media fallback. */
  errorDetail?: string;
  /** Optional text label for the open action. The default is icon-only. */
  openActionLabel?: string;
  /** Visual framing for the collapsed chip. */
  intent?: MediaTagIntent;
  /** Height of the collapsed chip. */
  size?: MediaTagSize;
  /** Controlled open state of the peek popover (used by stories/tests). */
  open?: boolean;
  /**
   * Fired when the popover opens/closes. The container arms its lazy fetch on
   * the `true` transition (VSCode-peek semantics: pull content on hover).
   */
  onOpenChange?: (open: boolean) => void;
}

type MediaKind = "image" | "audio" | "video" | "file";

function getMediaKind(contentType: string): MediaKind {
  const top = contentType.split("/")[0]?.toLowerCase();
  if (top === "image") return "image";
  if (top === "audio") return "audio";
  if (top === "video") return "video";
  return "file";
}

/** "image/svg+xml" -> "SVG", "application/pdf" -> "PDF". */
function getDefaultLabel(contentType: string): string {
  const subtype = contentType.split("/")[1]?.split("+")[0];
  return getMappedMediaLabel(contentType) ?? (subtype || "file").toUpperCase();
}

function formatFileSize(bytes: number): string {
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex++;
  }

  return `${value.toFixed(2)} ${units[unitIndex]}`;
}

const MEDIA_KIND_ICON = {
  image: ImageIcon,
  audio: Volume2,
  video: Video,
  file: File,
} satisfies Record<MediaKind, LucideIcon>;

const mediaTagVariants = cva(
  "focus-visible:ring-ring inline-flex w-fit max-w-full items-center gap-1 rounded-sm border px-1 py-0 align-middle text-xs leading-4 transition-colors focus-visible:ring-2 focus-visible:outline-hidden",
  {
    variants: {
      intent: {
        default: "hover:bg-accent bg-background",
        attachment:
          "border-blue-500/40 bg-blue-50 text-blue-800 hover:bg-blue-100 dark:bg-blue-950/30 dark:text-blue-200 dark:hover:bg-blue-950/50",
      },
      size: {
        sm: "h-3.5",
        md: "h-5",
      },
    },
    defaultVariants: {
      intent: "default",
      size: "md",
    },
  },
);

function KindIcon({
  kind,
  className,
}: {
  kind: MediaKind;
  className?: string;
}) {
  const Icon = MEDIA_KIND_ICON[kind];
  return <Icon className={className} />;
}

type PreviewRenderer = (params: {
  url: string;
  onError: () => void;
}) => React.ReactNode;

const MEDIA_KIND_PREVIEW = {
  image: ({ url, onError }) => (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={url}
      alt=""
      onError={onError}
      // Bounded by the max-h cap and the card's max-w-sm; object-contain
      // keeps high-resolution images from blowing out the popover.
      className="max-h-64 max-w-full rounded object-contain"
    />
  ),
  video: ({ url, onError }) => (
    <video
      src={url}
      onError={onError}
      className="max-h-64 max-w-full rounded"
      controls
      muted
      playsInline
      preload="metadata"
    />
  ),
  audio: ({ url, onError }) => (
    <audio
      src={url}
      onError={onError}
      controls
      className="w-64"
      preload="metadata"
    />
  ),
  file: () => (
    <div className="text-muted-foreground flex h-24 w-64 flex-col items-center justify-center gap-2">
      <File className="icon-lg" />
      <span className="text-xs">No inline preview</span>
    </div>
  ),
} satisfies Record<MediaKind, PreviewRenderer>;

/** The peek body — a glance, not a full player. Images/video show a thumbnail;
 *  audio gets an inline player; other types are open-in-new-tab only. */
function PeekBody({
  kind,
  status,
  url,
  errorDetail,
  onPreviewError,
}: {
  kind: MediaKind;
  status: MediaTagStatus;
  url?: string;
  errorDetail?: string;
  onPreviewError: () => void;
}) {
  if (status === "error") {
    return (
      <div className="text-muted-foreground flex min-h-24 w-64 flex-col items-center justify-center gap-2 p-2">
        <ImageOff className="icon-lg" />
        <span className="text-xs">Failed to load media</span>
        {errorDetail ? (
          <code className="max-w-full text-left text-[10px] leading-4 break-all select-text">
            {errorDetail}
          </code>
        ) : null}
      </div>
    );
  }

  if (status !== "ready" || !url) {
    return <Skeleton className="h-32 w-64" />;
  }

  return MEDIA_KIND_PREVIEW[kind]({ url, onError: onPreviewError });
}

/**
 * Pure, presentational media tag for use inside JSON viewers. Renders a compact
 * inline chip (icon + MIME label) that, on hover/focus, opens a read-only peek
 * popover with a preview and an "open in new tab" action.
 *
 * It receives the resolved `url` + `status` as props and never fetches — the
 * owning container (`MediaReferenceTag`) gates a lazy fetch on `onOpenChange`.
 */
export const MediaTag = React.forwardRef<HTMLButtonElement, MediaTagProps>(
  (
    {
      contentType,
      status = "idle",
      url,
      contentLength,
      label,
      description,
      errorDetail,
      openActionLabel,
      intent,
      size,
      open,
      onOpenChange,
    },
    ref,
  ) => {
    const kind = getMediaKind(contentType);
    const chipLabel = label ?? getDefaultLabel(contentType);
    const hasGeneratedLabel = label === undefined;
    const canOpen = status === "ready" && Boolean(url);
    const [failedPreviewUrl, setFailedPreviewUrl] = React.useState<
      string | null
    >(null);
    const previewStatus =
      status === "ready" && url && failedPreviewUrl === url ? "error" : status;
    const isControlled = open !== undefined;
    const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);
    const isOpen = isControlled ? open : uncontrolledOpen;

    const setOpen = React.useCallback(
      (nextOpen: boolean) => {
        if (!isControlled) setUncontrolledOpen(nextOpen);
        onOpenChange?.(nextOpen);
      },
      [isControlled, onOpenChange],
    );

    const openPeek = React.useCallback(() => setOpen(true), [setOpen]);

    return (
      <ControlledHoverCard
        open={isOpen}
        onOpenChange={setOpen}
        placement="bottom-start"
        onClick={(event) => event.stopPropagation()}
        content={
          <div className="ph-no-capture flex w-auto max-w-sm flex-col gap-2 p-2">
            <div className="flex items-center justify-between gap-4">
              <div className="text-muted-foreground flex min-w-0 items-center gap-1.5 text-xs">
                <KindIcon kind={kind} className="icon-base shrink-0" />
                <span
                  className="max-w-[20ch] truncate font-mono leading-4"
                  title={contentType}
                >
                  {contentType}
                </span>
                {contentLength !== undefined ? (
                  <>
                    <span aria-hidden="true">·</span>
                    <span className="shrink-0">
                      {formatFileSize(contentLength)}
                    </span>
                  </>
                ) : null}
              </div>
              {canOpen ? (
                <Button
                  asChild
                  variant="outline"
                  size={openActionLabel ? "sm" : "icon-xs"}
                  className={openActionLabel ? "gap-1.5" : undefined}
                  title="Open in new tab"
                >
                  <a href={url} target="_blank" rel="noopener noreferrer">
                    {openActionLabel ? <span>{openActionLabel}</span> : null}
                    <ExternalLink className="icon-sm text-icon-foreground" />
                  </a>
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size={openActionLabel ? "sm" : "icon-xs"}
                  className={openActionLabel ? "gap-1.5" : undefined}
                  disabled
                  title="Open in new tab"
                >
                  {openActionLabel ? <span>{openActionLabel}</span> : null}
                  <ExternalLink className="icon-sm text-icon-foreground" />
                </Button>
              )}
            </div>
            {description ? (
              <p className="text-muted-foreground max-w-64 text-xs">
                {description}
              </p>
            ) : null}
            <PeekBody
              kind={kind}
              status={previewStatus}
              url={url}
              errorDetail={errorDetail}
              onPreviewError={() => setFailedPreviewUrl(url ?? null)}
            />
          </div>
        }
      >
        {({ getTriggerProps }) => (
          <button
            type="button"
            // Containers detect chip hover via event delegation to suppress their own previews.
            data-media-tag=""
            aria-label={`${chipLabel} media`}
            className={mediaTagVariants({ intent, size })}
            {...getTriggerProps({
              ref,
              onClick: openPeek,
              onPointerDown: (event) => {
                if (event.pointerType !== "mouse") {
                  event.preventDefault();
                  openPeek();
                }
              },
            })}
          >
            {intent === "attachment" ? (
              <Paperclip className="icon-sm shrink-0" />
            ) : (
              <KindIcon kind={kind} className="icon-sm shrink-0" />
            )}
            <span
              className={cn(
                "truncate font-mono leading-4",
                hasGeneratedLabel && "max-w-[10ch]",
              )}
              // Suppress native tooltips while the peek is open.
              title={isOpen ? "" : hasGeneratedLabel ? contentType : chipLabel}
            >
              {chipLabel}
            </span>
          </button>
        )}
      </ControlledHoverCard>
    );
  },
);

MediaTag.displayName = "MediaTag";
